import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  Search, Package, Truck, CheckCircle, Clock, 
  MapPin, AlertCircle, Loader2, Box, ArrowRight
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { formatSelectedVariant } from '@/lib/variant';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Order Status Steps
const OrderStatusSteps = ({ currentStatus }) => {
  const { t } = useTranslation();
  
  const steps = [
    { id: 'pending', label: t('track.statusPending'), icon: Clock },
    { id: 'confirmed', label: t('track.statusConfirmed'), icon: CheckCircle },
    { id: 'shipped', label: t('track.statusShipped'), icon: Truck },
    { id: 'delivered', label: t('track.statusDelivered'), icon: Package },
  ];

  const statusOrder = ['pending', 'confirmed', 'processing', 'shipped', 'delivered'];
  const currentIndex = statusOrder.indexOf(currentStatus);

  return (
    <div className="flex items-center justify-between w-full max-w-2xl mx-auto">
      {steps.map((step, index) => {
        const Icon = step.icon;
        const isCompleted = currentIndex >= statusOrder.indexOf(step.id);
        const isCurrent = currentStatus === step.id || 
          (currentStatus === 'processing' && step.id === 'confirmed');
        
        return (
          <React.Fragment key={step.id}>
            <div className="flex flex-col items-center">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                isCompleted 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gray-200 text-gray-400'
              } ${isCurrent ? 'ring-4 ring-green-200' : ''}`}>
                <Icon className="w-6 h-6" />
              </div>
              <span className={`text-xs mt-2 text-center ${
                isCompleted ? 'text-green-600 font-medium' : 'text-gray-400'
              }`}>
                {step.label}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div className={`flex-1 h-1 mx-2 ${
                currentIndex > statusOrder.indexOf(step.id) 
                  ? 'bg-green-500' 
                  : 'bg-gray-200'
              }`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const OrderTrackingPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [error, setError] = useState(null);

  const runSearch = useCallback(async (num, mail) => {
    const on = (num || '').trim();
    if (!on) return;

    setIsSearching(true);
    setError(null);
    setOrderResult(null);

    try {
      const qs = new URLSearchParams({ order_number: on });
      if ((mail || '').trim()) qs.set('email', mail.trim());
      const response = await fetch(`${API_URL}/api/orders/track?${qs.toString()}`);

      if (response.ok) {
        const data = await response.json();
        setOrderResult(data);
      } else if (response.status === 404) {
        setError(t('track.notFound'));
      } else {
        setError(t('track.error'));
      }
    } catch (err) {
      console.error('Error tracking order:', err);
      setError(t('track.error'));
    } finally {
      setIsSearching(false);
    }
  }, [t]);

  const handleSearch = () => runSearch(orderNumber, email);

  useEffect(() => {
    const on = searchParams.get('order_number') || searchParams.get('order') || '';
    const em = searchParams.get('email') || '';
    if (!on) return;
    setOrderNumber(on);
    if (em) setEmail(em);
    runSearch(on, em);
  }, [searchParams, runSearch]);

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      processing: 'bg-purple-100 text-purple-800',
      shipped: 'bg-indigo-100 text-indigo-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="order-tracking-page">
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-primary transition-colors">
              {t('common.home')}
            </Link>
            <span>/</span>
            <span className="text-gray-900 font-medium">{t('track.pageTitle')}</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary to-primary/80 text-white py-12">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <h1 className="text-3xl md:text-4xl font-bold mb-4 flex items-center gap-3">
              <Package className="w-10 h-10" />
              {t('track.pageTitle')}
            </h1>
            <p className="text-lg text-white/90">
              {t('track.heroDescription')}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 md:px-8 py-12">
        
        {/* Search Section */}
        <section className="max-w-2xl mx-auto mb-12">
          <div className="bg-white rounded-xl p-6 md:p-8 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <Search className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">{t('track.searchTitle')}</h2>
                <p className="text-sm text-gray-600">{t('track.searchDescription')}</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('track.orderNumber')} *
                </label>
                <Input
                  type="text"
                  placeholder={t('track.orderNumberPlaceholder')}
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  className="h-12"
                  data-testid="order-number-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('track.email')} ({t('track.optional')})
                </label>
                <Input
                  type="email"
                  placeholder={t('track.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12"
                  data-testid="email-input"
                />
              </div>

              <Button 
                onClick={handleSearch} 
                disabled={!orderNumber.trim() || isSearching}
                className="w-full h-12"
                data-testid="track-button"
              >
                {isSearching ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Search className="w-4 h-4 mr-2" />
                )}
                {t('track.searchButton')}
              </Button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3" data-testid="track-error">
                <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
          </div>
        </section>

        {/* Order Result */}
        {orderResult && (
          <section className="max-w-3xl mx-auto mb-12" data-testid="order-result">
            <div className="bg-white rounded-xl p-6 md:p-8 shadow-sm border border-gray-100">
              {/* Order Header */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8 pb-6 border-b">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t('track.orderNumber')}: #{orderResult.order_number}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {t('track.orderDate')}: {new Date(orderResult.created_at).toLocaleDateString()}
                  </p>
                </div>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(orderResult.status)}`}>
                  {t(`track.status${orderResult.status.charAt(0).toUpperCase() + orderResult.status.slice(1)}`)}
                </span>
              </div>

              {/* Status Progress */}
              {orderResult.status !== 'cancelled' && (
                <div className="mb-8">
                  <OrderStatusSteps currentStatus={orderResult.status} />
                </div>
              )}

              {/* Order Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Shipping Address */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="w-4 h-4 text-primary" />
                    <h4 className="font-medium text-gray-900">{t('track.shippingAddress')}</h4>
                  </div>
                  <p className="text-sm text-gray-600">
                    {orderResult.shipping_address?.full_name}<br />
                    {orderResult.shipping_address?.address_line1 || orderResult.shipping_address?.address}<br />
                    {orderResult.shipping_address?.address_line2 && (
                      <>{orderResult.shipping_address.address_line2}<br /></>
                    )}
                    {orderResult.shipping_address?.city}
                    {orderResult.shipping_address?.state ? `, ${orderResult.shipping_address.state}` : ''}
                    {orderResult.shipping_address?.postal_code ? ` ${orderResult.shipping_address.postal_code}` : ''}
                    <br />
                    {orderResult.shipping_address?.country}
                  </p>
                </div>

                {/* Order Summary */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Box className="w-4 h-4 text-primary" />
                    <h4 className="font-medium text-gray-900">{t('track.orderSummary')}</h4>
                  </div>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>{t('track.itemCount')}: {orderResult.items?.length || 0}</p>
                    {orderResult.subtotal != null && (
                      <p>{t('cart.subtotal')}: {Number(orderResult.subtotal).toFixed(2)} {orderResult.currency || 'TRY'}</p>
                    )}
                    {orderResult.tax > 0 && (
                      <p>{t('cart.tax')}: {Number(orderResult.tax).toFixed(2)} {orderResult.currency || 'TRY'}</p>
                    )}
                    {orderResult.shipping_cost > 0 && (
                      <p>{t('cart.shipping')}: {Number(orderResult.shipping_cost).toFixed(2)} {orderResult.currency || 'TRY'}</p>
                    )}
                    <p className="font-semibold">{t('track.total')}: {Number(orderResult.total || 0).toFixed(2)} {orderResult.currency || 'TRY'}</p>
                    {orderResult.tracking_number && (
                      <p className="font-medium text-primary">
                        {t('track.trackingNumber')}: {orderResult.tracking_number}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Items List */}
              {orderResult.items && orderResult.items.length > 0 && (
                <div className="mt-6 pt-6 border-t">
                  <h4 className="font-medium text-gray-900 mb-4">{t('track.items')}</h4>
                  <div className="space-y-3">
                    {orderResult.items.map((item, index) => (
                      <div key={index} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                        {item.image && (
                          <img 
                            src={item.image} 
                            alt={item.name} 
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{item.name}</p>
                          {formatSelectedVariant(item.selected_variant) && (
                            <p className="text-sm text-blue-700">{formatSelectedVariant(item.selected_variant)}</p>
                          )}
                          <p className="text-sm text-gray-500">
                            {t('track.quantity')}: {item.quantity} × {item.price?.toFixed(2)} {orderResult.currency || '€'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Help Section */}
        <section className="max-w-2xl mx-auto">
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl p-6 md:p-8 text-center">
            <Package className="w-12 h-12 text-primary mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">{t('track.needHelp')}</h3>
            <p className="text-gray-400 mb-6">{t('track.needHelpDesc')}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/contact">
                <Button className="px-6" data-testid="contact-btn">
                  {t('footer.contactUs')}
                </Button>
              </Link>
              <Link to="/shipping">
                <Button variant="outline" className="px-6 border-gray-600 text-white hover:bg-gray-700">
                  {t('footer.shippingInfo')}
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default OrderTrackingPage;
